import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Avatar,
  Grid,
  Paper,
  Divider,
  useTheme,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { Person, Edit, Lock } from '@mui/icons-material';
import axios from 'axios';
import config from './global/config';
import { ReactSession } from 'react-client-session';
import { tokens } from '../dashboard/theme';

const UserProfile = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const navigate = useNavigate();
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newUsername, setNewUsername] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [openPasswordDialog, setOpenPasswordDialog] = useState(false);

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        setLoading(true);
        const userId = ReactSession.get("idUser");
        if (!userId) {
          setError('User ID not found. Please log in again.');
          setLoading(false);
          return;
        }

        const response = await axios.get(`${config.apiUrl}/user.php?idUser=${userId}`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        });
        
        if (response.data && response.data[0]) {
          setUserData(response.data[0]);
          setNewUsername(response.data[0].username);
        } else {
          setError('User data not found');
        }
      } catch (err) {
        console.error('Error fetching user data:', err);
        setError('Failed to load user data: ' + (err.response?.data?.message || err.message));
      } finally {
        setLoading(false);
      }
    };
    fetchUserData();
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('token');
    ReactSession.remove("idUser");
    ReactSession.remove("username");
    navigate('/login');
  };

  const handleUsernameUpdate = async (e) => {
    e.preventDefault();
    if (!userData) return;
    
    setError('');
    setSuccess('');

    try {
      const userId = ReactSession.get("idUser");
      const data = {
        id: userId,
        action: 'update_username',
        username: newUsername
      };

      const response = await axios.put(
        `${config.apiUrl}/user.php`,
        data,
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        }
      );

      if (response.data && response.data.error) {
        setError(response.data.message || 'Failed to update username');
        return;
      }

      setSuccess('Username updated successfully');
      setUserData(prev => ({ ...prev, username: newUsername }));
      ReactSession.set("username", newUsername);
    } catch (err) {
      console.error('Error updating username:', err);
      setError(err.response?.data?.message || 'Failed to update username');
    }
  };

  const handlePasswordUpdate = async (e) => {
    e.preventDefault();
    if (!userData) return;

    setError('');
    setSuccess('');

    if (newPassword !== confirmPassword) {
      setError('New passwords do not match');
      return;
    }

    try {
      const userId = ReactSession.get("idUser");
      const formData = new FormData();
      formData.append('password', newPassword);
      formData.append('user_upd', ReactSession.get("username"));
      formData.append('first_login', '0');
      formData.append('METHOD', 'PUT');

      const response = await axios.post(
        `${config.apiUrl}/updates.php`,
        formData,
        {
          params: { idUser: userId },
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
          withCredentials: false
        }
      );

      if (response.data && response.data.error) {
        setError(response.data.message || 'Failed to update password');
        return;
      }

      setSuccess('Password updated successfully. You will be logged out in 3 seconds...');
      setTimeout(() => {
        handleLogout();
      }, 3000);
    } catch (err) {
      console.error('Error updating password:', err);
      setError(err.response?.data?.message || 'Failed to update password');
    }
  };

  const handleOpenPasswordDialog = () => {
    setOpenPasswordDialog(true);
    setError('');
  };

  const handleClosePasswordDialog = () => {
    setOpenPasswordDialog(false);
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setError('');
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  if (!userData) {
    return (
      <Box m="25px">
        <Alert severity="error">{error || 'Failed to load user profile'}</Alert>
      </Box>
    );
  }

  return (
    <Box m="25px">
      <Paper elevation={3} sx={{ p: 3, backgroundColor: colors.primary[900] }}>
        <Grid container spacing={3}>
          {/* Profile Header */}
          <Grid item xs={12} display="flex" alignItems="center" gap={2}>
            <Avatar
              sx={{
                width: 80,
                height: 80,
                bgcolor: colors.greenAccent[500],
                fontSize: '2rem'
              }}
            >
              {userData.username ? userData.username[0].toUpperCase() : 'U'}
            </Avatar>
            <Box>
              <Typography variant="h4" color={colors.grey[100]}>
                {userData.username}
              </Typography>
              <Typography variant="subtitle1" color={colors.grey[300]}>
                {userData.role}
              </Typography>
            </Box>
          </Grid>

          {/* Alerts */}
          <Grid item xs={12}>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
          </Grid>

          {/* Username Update Section */}
          <Grid item xs={12} md={6}>
            <Card sx={{ height: '100%', backgroundColor: colors.primary[900] }}>
              <CardContent>
                <Box display="flex" alignItems="center" gap={1} mb={2}>
                  <Person color="primary" />
                  <Typography variant="h5" color={colors.grey[100]}>
                    Update Username
                  </Typography>
                </Box>
                <Divider sx={{ mb: 2 }} />
                <Box component="form" onSubmit={handleUsernameUpdate}>
                  <TextField
                    fullWidth
                    label="New Username"
                    value={newUsername}
                    onChange={(e) => setNewUsername(e.target.value)}
                    margin="normal"
                    required
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': {
                          borderColor: colors.grey[100],
                        },
                      },
                      '& .MuiInputLabel-root': {
                        color: colors.grey[100],
                      },
                      '& .MuiOutlinedInput-input': {
                        color: colors.grey[100],
                      },
                    }}
                  />
                  <Button
                    type="submit"
                    variant="contained"
                    color="secondary"
                    startIcon={<Edit />}
                    sx={{ mt: 2 }}
                  >
                    Update Username
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Password Update Section */}
          <Grid item xs={12} md={6}>
            <Card sx={{ height: '100%', backgroundColor: colors.primary[900] }}>
              <CardContent>
                <Box display="flex" alignItems="center" gap={1} mb={2}>
                  <Lock color="primary" />
                  <Typography variant="h5" color={colors.grey[100]}>
                    Password Settings
                  </Typography>
                </Box>
                <Divider sx={{ mb: 2 }} />
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={handleOpenPasswordDialog}
                  startIcon={<Lock />}
                >
                  Change Password
                </Button>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Paper>

      {/* Password Update Dialog */}
      <Dialog 
        open={openPasswordDialog} 
        onClose={handleClosePasswordDialog}
        PaperProps={{
          sx: {
            backgroundColor: colors.primary[900],
            color: colors.grey[100],
          }
        }}
      >
        <DialogTitle sx={{ color: colors.grey[100] }}>Change Password</DialogTitle>
        <DialogContent>
          <Box component="form" onSubmit={handlePasswordUpdate}>
            {userData.role !== 'admin' && (
              <TextField
                fullWidth
                type="password"
                label="Current Password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                margin="normal"
                required
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': {
                      borderColor: colors.grey[100],
                    },
                  },
                  '& .MuiInputLabel-root': {
                    color: colors.grey[100],
                  },
                  '& .MuiOutlinedInput-input': {
                    color: colors.grey[100],
                  },
                }}
              />
            )}
            <TextField
              fullWidth
              type="password"
              label="New Password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              margin="normal"
              required
              sx={{
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderColor: colors.grey[100],
                  },
                },
                '& .MuiInputLabel-root': {
                  color: colors.grey[100],
                },
                '& .MuiOutlinedInput-input': {
                  color: colors.grey[100],
                },
              }}
            />
            <TextField
              fullWidth
              type="password"
              label="Confirm New Password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              margin="normal"
              required
              sx={{
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderColor: colors.grey[100],
                  },
                },
                '& .MuiInputLabel-root': {
                  color: colors.grey[100],
                },
                '& .MuiOutlinedInput-input': {
                  color: colors.grey[100],
                },
              }}
            />
            <DialogActions>
              <Button onClick={handleClosePasswordDialog} sx={{ color: colors.grey[100] }}>
                Cancel
              </Button>
              <Button type="submit" variant="contained" color="secondary">
                Update Password
              </Button>
            </DialogActions>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default UserProfile;
